import React, { useState, useEffect } from 'react';
import { 
  Plus, Settings, Eye, Edit, Trash2, Save, X, BarChart3, Users, MessageSquare, 
  Bell, ChevronDown, ChevronRight, AlertCircle, CheckCircle, Clock, Target, 
  Link2, Activity, TrendingUp, RefreshCw, Filter, Search, FileText 
} from 'lucide-react';
import { 
  getPulseConfiguration, 
  updatePulseConfiguration, 
  getPulseSurveys, 
  createPulseSurvey, 
  updatePulseSurvey, 
  deletePulseSurvey, 
  getPulseAnalyticsWithRealTracking,
  getSurveyResponsesAnalytics
} from '../utils/adminApi';
import PulseAdmin from './PulseAdmin';
import '../styles/SmartPulseAdmin.css';

const SmartPulseAdmin = ({ adminUser }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for configuration
  const [pulseConfig, setPulseConfig] = useState({
    include_insights: true,
    insight_position: 0,
    include_survey: true,
    survey_position: 1,
    include_prompts: true,
    prompt_position: 2
  });

  // State for surveys
  const [surveys, setSurveys] = useState([]);
  const [smartPrompts, setSmartPrompts] = useState([]);
  const [analytics, setAnalytics] = useState({
    totalPulseViews: 0,
    smartContentShown: 0,
    smartContentClicks: 0,
    conversionRate: 0,
    topPerformingPrompt: '',
    activeUsers: 0
  });

  // Trigger conditions for smart prompts
  const triggerConditions = [
    { value: 'profile_incomplete', label: 'Profile Incomplete' },
    { value: 'no_artworks', label: 'No Artworks Uploaded' },
    { value: 'no_follows', label: 'Not Following Anyone' },
    { value: 'no_gallery_visits', label: 'No Gallery Visits' },
    { value: 'inactive_user', label: 'Inactive for 7+ Days' },
    { value: 'new_user', label: 'New User (< 24 hours)' },
    { value: 'no_bookmarks', label: 'No Saved Artworks' },
    { value: 'custom', label: 'Custom SQL Condition' }
  ];

  // Load data on component mount
  useEffect(() => {
    loadPulseData();
  }, []);

  /**
   * Load pulse configuration and data using adminApi functions
   */
  const loadPulseData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load configuration, surveys, and analytics in parallel
      const [configData, surveysData, analyticsData] = await Promise.all([
        getPulseConfiguration(),
        getPulseSurveys(),
        getPulseAnalyticsWithRealTracking('30d')
      ]);

      setPulseConfig(configData);
      setSurveys(surveysData);
      setAnalytics(analyticsData);
      
    } catch (err) {
      console.error('Error loading pulse data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Save pulse configuration using adminApi
   */
  const savePulseConfig = async (newConfig) => {
    try {
      await updatePulseConfiguration(newConfig, adminUser);
      setPulseConfig(newConfig);
      return true;
    } catch (err) {
      console.error('Error saving pulse config:', err);
      setError(err.message);
      return false;
    }
  };

  /**
   * Create new survey using adminApi
   */
  const createSurvey = async (surveyData) => {
    try {
      const newSurvey = await createPulseSurvey(surveyData, adminUser);
      setSurveys(prev => [newSurvey, ...prev]);
      return true;
    } catch (err) {
      console.error('Error creating survey:', err);
      setError(err.message);
      return false;
    }
  };

  /**
   * Delete survey using adminApi
   */
  const deleteSurvey = async (surveyId) => {
    try {
      await deletePulseSurvey(surveyId, adminUser);
      setSurveys(prev => prev.filter(s => s.preference_id !== surveyId));
      return true;
    } catch (err) {
      console.error('Error deleting survey:', err);
      setError(err.message);
      return false;
    }
  };

  /**
   * Dashboard Tab Component
   */
  const DashboardTab = () => (
    <div className="smart-pulse-dashboard">
      {/* Analytics Overview */}
      <div className="dashboard-stats-grid">
        <div className="dashboard-stat-card">
                      <div className="stat-icon-wrapper blue">
              <Eye className="stat-icon" color="#007bff" />
            </div>
          <div className="stat-content">
            <h3>{analytics.totalPulseViews.toLocaleString()}</h3>
            <p>Total Pulse Views</p>
            <span className="stat-trend positive">+12% this week</span>
          </div>
        </div>
        
        <div className="dashboard-stat-card">
                      <div className="stat-icon-wrapper green">
              <Target className="stat-icon" color="#28a745" />
            </div>
          <div className="stat-content">
            <h3>{analytics.smartContentShown.toLocaleString()}</h3>
            <p>Smart Content Shown</p>
            <span className="stat-trend positive">+8% this week</span>
          </div>
        </div>
        
        <div className="dashboard-stat-card">
          <div className="stat-icon-wrapper purple">
            <Link2 className="stat-icon" color="#6f42c1" />
          </div>
          <div className="stat-content">
            <h3>{analytics.smartContentClicks.toLocaleString()}</h3>
            <p>Smart Content Clicks</p>
            <span className="stat-trend positive">+24% this week</span>
          </div>
        </div>
        
        <div className="dashboard-stat-card">
          <div className="stat-icon-wrapper orange">
            <BarChart3 className="stat-icon" color="#fd7e14" />
          </div>
          <div className="stat-content">
            <h3>{analytics.conversionRate}%</h3>
            <p>Conversion Rate</p>
            <span className="stat-trend positive">+2.1% this week</span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="dashboard-quick-actions">
        <h3>Quick Actions</h3>
        <div className="quick-actions-grid">
          <button 
            onClick={() => setShowPromptModal(true)}
            className="quick-action-btn"
          >
            <Plus className="action-icon" color="#28a745" />
            <span>Create Smart Prompt</span>
          </button>
          
          <button 
            onClick={() => setShowSurveyModal(true)}
            className="quick-action-btn"
          >
            <MessageSquare className="action-icon" color="#007bff" />
            <span>Create Survey</span>
          </button>
          
          <button 
            onClick={() => setShowConfigModal(true)}
            className="quick-action-btn"
          >
            <Settings className="action-icon" color="#6c757d" />
            <span>Configure Settings</span>
          </button>
        </div>
      </div>

      {/* Recent Performance */}
      <div className="dashboard-recent-performance">
        <div className="section-header">
          <h3>Recent Smart Content Performance</h3>
          <button onClick={loadPulseData} className="refresh-btn">
            <RefreshCw className="refresh-icon" color="#6c757d" />
          </button>
        </div>
        
        <div className="performance-list">
          {surveys.slice(0, 3).map(survey => (
            <div key={survey.preference_id} className="performance-item">
              <div className="performance-content">
                <h4>{survey.question}</h4>
                <p>Survey for {survey.user_type} users • Created {new Date(survey.created_at).toLocaleDateString()}</p>
              </div>
              <div className="performance-stats">
                <span className="ctr-rate">Active</span>
                <span className="time-period">{survey.question_type}</span>
              </div>
            </div>
          ))}
          
          {surveys.length === 0 && (
            <div className="empty-state">
              <p>No surveys created yet. <button onClick={() => setShowSurveyModal(true)}>Create your first survey</button></p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  /**
   * Configuration Tab Component
   */
  const ConfigurationTab = () => (
    <div className="smart-pulse-configuration">
      <div className="configuration-header">
        <h2>Pulse Configuration</h2>
        <button 
          onClick={() => setShowConfigModal(true)}
          className="config-edit-btn"
        >
          <Settings className="btn-icon" color="#6c757d" />
          Edit Settings
        </button>
      </div>

      <div className="configuration-content">
        <h3>Current Configuration</h3>
        <div className="config-items">
          <div className="config-item">
            <div className="config-info">
              <h4>User Discovery Insights</h4>
              <p>Personalized recommendations for logged-in users</p>
            </div>
            <div className="config-status">
              <span className="position-badge">Position: {pulseConfig.insight_position}</span>
              <span className={`status-badge ${pulseConfig.include_insights ? 'enabled' : 'disabled'}`}>
                {pulseConfig.include_insights ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>

          <div className="config-item">
            <div className="config-info">
              <h4>User Surveys</h4>
              <p>Random surveys based on user type</p>
            </div>
            <div className="config-status">
              <span className="position-badge">Position: {pulseConfig.survey_position}</span>
              <span className={`status-badge ${pulseConfig.include_survey ? 'enabled' : 'disabled'}`}>
                {pulseConfig.include_survey ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>

          <div className="config-item">
            <div className="config-info">
              <h4>Smart Prompts</h4>
              <p>Contextual prompts based on user behavior</p>
            </div>
            <div className="config-status">
              <span className="position-badge">Position: {pulseConfig.prompt_position}</span>
              <span className={`status-badge ${pulseConfig.include_prompts ? 'enabled' : 'disabled'}`}>
                {pulseConfig.include_prompts ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
        </div>

        <div className="config-note">
          <AlertCircle className="note-icon" color="#fd7e14" />
          <div>
            <h4>Configuration Notes</h4>
            <p>
              Position numbers determine the order of smart content injection in the pulse feed. 
              Lower numbers appear earlier in the feed (position 0 = first item).
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  /**
   * Surveys Tab Component
   */
  const SurveysTab = () => (
    <div className="smart-pulse-surveys">
      <div className="surveys-header">
        <h2>Pulse Surveys</h2>
        <button 
          onClick={() => setShowSurveyModal(true)}
          className="create-survey-btn"
        >
          <Plus className="btn-icon" color="#28a745" />
          Create Survey
        </button>
      </div>

      <div className="surveys-list">
        {surveys.map(survey => (
          <div key={survey.preference_id} className="survey-card">
            <div className="survey-content">
              <div className="survey-header">
                <h3>{survey.question}</h3>
                <div className="survey-badges">
                  <span className={`status-badge ${survey.featured_on_pulse ? 'enabled' : 'disabled'}`}>
                    {survey.featured_on_pulse ? 'Featured' : 'Draft'}
                  </span>
                  <span className="user-type-badge">{survey.user_type}</span>
                </div>
              </div>
              
              <div className="survey-options">
                <p className="options-label">Options:</p>
                <div className="options-list">
                  {Array.isArray(survey.options) ? survey.options.map((option, idx) => (
                    <span key={idx} className="option-tag">{option}</span>
                  )) : (
                    <span className="option-tag">Text Response</span>
                  )}
                </div>
              </div>
              
              <div className="survey-meta">
                <span>Type: {survey.question_type}</span>
                <span>•</span>
                <span>Created: {new Date(survey.created_at).toLocaleDateString()}</span>
              </div>
            </div>
            
            <div className="survey-actions">
              <button 
                className="action-btn"
                onClick={async () => {
                  const responses = await getSurveyResponsesAnalytics(survey.preference_id);
                  alert(`Total responses: ${responses.totalResponses}`);
                }}
              >
                <BarChart3 className="action-icon" color="#007bff" />
              </button>
              <button className="action-btn">
                <Edit className="action-icon" color="#6c757d" />
              </button>
              <button 
                className="action-btn danger"
                onClick={async () => {
                  if (window.confirm('Are you sure you want to delete this survey?')) {
                    await deleteSurvey(survey.preference_id);
                  }
                }}
              >
                <Trash2 className="action-icon" color="#dc3545" />
              </button>
            </div>
          </div>
        ))}
        
        {surveys.length === 0 && (
                  <div className="empty-state">
          <MessageSquare className="empty-icon" color="#6c757d" />
          <h3>No surveys created yet</h3>
          <p>Create your first survey to start collecting user feedback in the pulse feed.</p>
          <button onClick={() => setShowSurveyModal(true)} className="create-first-btn">
            Create Survey
          </button>
        </div>
        )}
      </div>
    </div>
  );

  /**
   * Configuration Modal Component
   */
  const ConfigModal = () => {
    const [tempConfig, setTempConfig] = useState(pulseConfig);

    const handleSave = async () => {
      const success = await savePulseConfig(tempConfig);
      if (success) {
        setShowConfigModal(false);
      }
    };

    if (!showConfigModal) return null;

    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <h3>Pulse Configuration</h3>
            <button onClick={() => setShowConfigModal(false)} className="close-btn">
              <X className="close-icon" color="#6c757d" />
            </button>
          </div>
          
          <div className="modal-body">
            <div className="config-form">
              <div className="form-group">
                <label>
                  <input 
                    type="checkbox" 
                    checked={tempConfig.include_insights}
                    onChange={(e) => setTempConfig({...tempConfig, include_insights: e.target.checked})}
                  />
                  Include Discovery Insights
                </label>
              </div>
              
              <div className="form-group">
                <label>Insight Position</label>
                <input 
                  type="number" 
                  value={tempConfig.insight_position}
                  onChange={(e) => setTempConfig({...tempConfig, insight_position: parseInt(e.target.value)})}
                  className="form-input"
                  min="0"
                  max="10"
                />
              </div>
              
              <div className="form-group">
                <label>
                  <input 
                    type="checkbox" 
                    checked={tempConfig.include_survey}
                    onChange={(e) => setTempConfig({...tempConfig, include_survey: e.target.checked})}
                  />
                  Include Surveys
                </label>
              </div>
              
              <div className="form-group">
                <label>Survey Position</label>
                <input 
                  type="number" 
                  value={tempConfig.survey_position}
                  onChange={(e) => setTempConfig({...tempConfig, survey_position: parseInt(e.target.value)})}
                  className="form-input"
                  min="0"
                  max="10"
                />
              </div>
            </div>
          </div>
          
          <div className="modal-footer">
            <button 
              onClick={() => setShowConfigModal(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button 
              onClick={handleSave}
              className="btn-primary"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Survey Modal Component
   */
  const SurveyModal = () => {
    const [newSurvey, setNewSurvey] = useState({
      question: '',
      question_type: 'multiple_choice',
      user_type: 'all',
      options: [''],
      allow_other: false,
      featured_on_pulse: true
    });

    const addOption = () => {
      setNewSurvey({...newSurvey, options: [...newSurvey.options, '']});
    };

    const updateOption = (index, value) => {
      const newOptions = [...newSurvey.options];
      newOptions[index] = value;
      setNewSurvey({...newSurvey, options: newOptions});
    };

    const removeOption = (index) => {
      const newOptions = newSurvey.options.filter((_, i) => i !== index);
      setNewSurvey({...newSurvey, options: newOptions});
    };

    const handleSave = async () => {
      const success = await createSurvey(newSurvey);
      if (success) {
        setShowSurveyModal(false);
        setNewSurvey({
          question: '',
          question_type: 'multiple_choice',
          user_type: 'all',
          options: [''],
          allow_other: false,
          featured_on_pulse: true
        });
      }
    };

    if (!showSurveyModal) return null;

    return (
      <div className="modal-overlay">
        <div className="modal-content large">
          <div className="modal-header">
            <h3>Create Survey</h3>
            <button onClick={() => setShowSurveyModal(false)} className="close-btn">
              <X className="close-icon" />
            </button>
          </div>
          
          <div className="modal-body">
            <div className="survey-form">
              <div className="form-group">
                <label>Question</label>
                <input 
                  type="text" 
                  value={newSurvey.question}
                  onChange={(e) => setNewSurvey({...newSurvey, question: e.target.value})}
                  placeholder="What would you like to ask users?"
                  className="form-input"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Question Type</label>
                  <select 
                    value={newSurvey.question_type}
                    onChange={(e) => setNewSurvey({...newSurvey, question_type: e.target.value})}
                    className="form-select"
                  >
                    <option value="multiple_choice">Multiple Choice</option>
                    <option value="single_choice">Single Choice</option>
                    <option value="text">Text Response</option>
                    <option value="rating">Rating Scale</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Target Users</label>
                  <select 
                    value={newSurvey.user_type}
                    onChange={(e) => setNewSurvey({...newSurvey, user_type: e.target.value})}
                    className="form-select"
                  >
                    <option value="all">All Users</option>
                    <option value="artist">Artists</option>
                    <option value="collector">Collectors</option>
                    <option value="gallery">Galleries</option>
                  </select>
                </div>
              </div>

              {(newSurvey.question_type === 'multiple_choice' || newSurvey.question_type === 'single_choice') && (
                <div className="form-group">
                  <label>Answer Options</label>
                  <div className="options-builder">
                    {newSurvey.options.map((option, index) => (
                      <div key={index} className="option-input-row">
                        <input 
                          type="text" 
                          value={option}
                          onChange={(e) => updateOption(index, e.target.value)}
                          placeholder={`Option ${index + 1}`}
                          className="form-input"
                        />
                        {newSurvey.options.length > 1 && (
                          <button 
                            onClick={() => removeOption(index)}
                            className="remove-option-btn"
                          >
                            <X className="remove-icon" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button onClick={addOption} className="add-option-btn">
                      + Add Option
                    </button>
                  </div>
                </div>
              )}

              <div className="form-checkboxes">
                <label>
                  <input 
                    type="checkbox" 
                    checked={newSurvey.allow_other}
                    onChange={(e) => setNewSurvey({...newSurvey, allow_other: e.target.checked})}
                  />
                  Allow "Other" option
                </label>

                <label>
                  <input 
                    type="checkbox" 
                    checked={newSurvey.featured_on_pulse}
                    onChange={(e) => setNewSurvey({...newSurvey, featured_on_pulse: e.target.checked})}
                  />
                  Feature in pulse feed
                </label>
              </div>
            </div>
          </div>
          
          <div className="modal-footer">
            <button 
              onClick={() => setShowSurveyModal(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button 
              onClick={handleSave}
              className="btn-primary"
            >
              Create Survey
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Show loading state
  if (loading) {
    return (
      <div className="smart-pulse-loading">
        <div className="loading-spinner"></div>
        <p>Loading Smart Pulse Admin...</p>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="smart-pulse-error">
        <AlertCircle className="error-icon" />
        <h3>Error Loading Smart Pulse Admin</h3>
        <p>{error}</p>
        <button onClick={loadPulseData} className="retry-btn">
          Try Again
        </button>
      </div>
    );
  }

  // Main component render
  return (
    <div className="smart-pulse-admin">
      {/* Tab Navigation */}
      <div className="pulse-tab-navigation">
        {[
          { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
          { id: 'posts', label: 'Pulse Posts', icon: FileText },
          { id: 'surveys', label: 'Surveys', icon: MessageSquare },
          { id: 'configuration', label: 'Configuration', icon: Settings }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`pulse-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
          >
            <tab.icon className="tab-icon" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="pulse-tab-content">
        {activeTab === 'dashboard' && <DashboardTab />}
        {activeTab === 'posts' && <PulseAdmin adminUser={adminUser} />}
        {activeTab === 'surveys' && <SurveysTab />}
        {activeTab === 'configuration' && <ConfigurationTab />}
      </div>

      {/* Modals */}
      <ConfigModal />
      <SurveyModal />
    </div>
  );
};

export default SmartPulseAdmin;